import { Suspense, useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { eventsApi } from "@/api/events";
import { useAuthStore } from "@/stores/auth.store";
import { t, Icon, ICONS, Avatar, Spinner } from "@/components/ui";
import { getStoredActiveId, setStoredActiveId } from "@/lib/activeEvent";
import type { Event } from "@/types";
import ExportsPanel from "@/features/exports/ExportsPanel";

const EVENT_NAV = [
  { to: "/dashboard",  label: "Dashboard",   icon: "grid",      end: true  },
  { to: "/agenda",     label: "Agenda",      icon: "calendar",  end: false },
  { to: "/runofshow",  label: "Run of Show", icon: "activity",  end: false },
  { to: "/speakers",   label: "Speakers",    icon: "users",     end: false },
  { to: "/messages",   label: "Messages",    icon: "mail",      end: false },
  { to: "/feedback",   label: "Feedback",    icon: "star",      end: false },
];

const ORG_NAV = [
  { to: "/events",   label: "Events",                icon: "globe",    end: false },
  { to: "/settings", label: "Organisation settings", icon: "settings", end: false },
];

const SIDEBAR_WIDTH = 248;
const MOBILE_BREAKPOINT = 900;

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  const clearAuth = useAuthStore(s => s.clearAuth);

  const [activeEventId, setActiveEventId] = useState<string | null>(getStoredActiveId);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Track viewport size for the responsive sidebar mode.
  useEffect(() => {
    function onResize() {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setDrawerOpen(false);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close the mobile drawer whenever the user navigates.
  useEffect(() => {
    if (isMobile) setDrawerOpen(false);
  }, [location.pathname, isMobile]);

  const { data: events = [] } = useQuery<Event[]>({
    queryKey: ["events"],
    queryFn: eventsApi.list,
  });

  const activeEvent =
    (activeEventId ? events.find(e => e.id === activeEventId) : null) ??
    events.find(e => e.status === "active") ??
    events[0];

  function switchToEvent(id: string) {
    setActiveEventId(id);
    setStoredActiveId(id);
    setShowSwitcher(false);
  }

  function signOut() {
    clearAuth();
    navigate("/login");
  }

  const nonArchivedEvents = events.filter(e => e.status !== "archived");

  // Sidebar visibility:
  //   desktop → always visible, in-flow
  //   mobile  → off-canvas drawer, slides in from left
  const sidebarVisible = !isMobile || drawerOpen;

  const sidebar = (
    <aside style={{
      width: SIDEBAR_WIDTH,
      background: t.sidebarBgGradient,
      color: t.sidebarText,
      display: "flex", flexDirection: "column",
      flexShrink: 0,
      borderRight: `1px solid ${t.sidebarBorder}`,
      ...(isMobile ? {
        position: "fixed",
        top: 0, bottom: 0, left: 0,
        zIndex: 60,
        transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
        transition: `transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
        boxShadow: drawerOpen ? "0 24px 60px rgba(0,0,0,0.45)" : "none",
      } : {}),
    }}>
      {/* Brand */}
      <div style={{
        padding: "20px 18px 16px",
        display: "flex", alignItems: "center", gap: 10,
        borderBottom: `1px solid ${t.sidebarBorder}`,
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 800, fontSize: 15,
          fontFamily: t.fontSans, letterSpacing: "-0.02em",
          boxShadow: "0 4px 14px rgba(139, 92, 246, 0.35)",
        }}>
          E
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
          <span style={{
            fontSize: 15, fontWeight: 700, color: "#fff",
            letterSpacing: "-0.02em",
          }}>
            EventFlow
          </span>
          <span style={{
            fontSize: 9, fontWeight: 700,
            color: t.accent, background: "rgba(99, 102, 241, 0.18)",
            padding: "2px 6px", borderRadius: t.radiusPill,
            fontFamily: t.fontMono, letterSpacing: "0.06em",
          }}>
            AI
          </span>
        </div>
        {isMobile && (
          <button
            onClick={() => setDrawerOpen(false)}
            title="Fermer"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: t.sidebarText, padding: 6, borderRadius: t.radiusSm,
              display: "flex",
            }}
          >
            <Icon d={ICONS.x} size={16} />
          </button>
        )}
      </div>

      {/* Event switcher — primary control under the brand */}
      <div style={{ position: "relative", padding: "12px 10px" }}>
        <button
          onClick={() => setShowSwitcher(v => !v)}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            width: "100%", padding: "10px 12px",
            background: showSwitcher ? "rgba(255, 255, 255, 0.06)" : "rgba(255, 255, 255, 0.03)",
            border: `1px solid ${showSwitcher ? "rgba(99, 102, 241, 0.4)" : "rgba(255, 255, 255, 0.06)"}`,
            borderRadius: t.radiusMd,
            cursor: "pointer", textAlign: "left",
            color: t.sidebarText, fontFamily: t.fontSans,
            transition: `background ${t.transitionFast}, border-color ${t.transitionFast}`,
          }}
        >
          {activeEvent ? (
            <>
              <div
                style={{
                  width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                  background: activeEvent.status === "active" ? t.success : t.sidebarTextMuted,
                  boxShadow: activeEvent.status === "active" ? `0 0 8px ${t.success}80` : "none",
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: "#fff",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  letterSpacing: "-0.01em",
                }}>
                  {activeEvent.name}
                </div>
                <div style={{
                  fontSize: 10, color: t.sidebarTextMuted, marginTop: 2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {new Date(activeEvent.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}
                  {activeEvent.city ? ` · ${activeEvent.city}` : ""}
                </div>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, fontSize: 12, color: t.sidebarTextMuted, fontStyle: "italic" }}>
              No event selected
            </div>
          )}
          <div style={{
            transform: showSwitcher ? "rotate(180deg)" : "rotate(0deg)",
            transition: `transform ${t.transitionFast}`,
            flexShrink: 0,
            color: t.sidebarTextMuted,
            display: "flex",
          }}>
            <Icon d={ICONS.chevronDown} size={13} />
          </div>
        </button>

        {showSwitcher && (
          <div style={{
            position: "absolute", top: "100%", left: 10, right: 10, zIndex: 50,
            background: "#1f2130",
            border: `1px solid ${t.sidebarBorder}`,
            borderRadius: t.radiusMd,
            marginTop: 4,
            boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
            maxHeight: 360, overflowY: "auto",
            padding: 4,
          }}>
            {/* All events — first option, always visible */}
            <button
              onClick={() => { setShowSwitcher(false); navigate("/events"); }}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                width: "100%", padding: "9px 10px",
                background: "transparent", border: "none",
                borderRadius: 6, cursor: "pointer", textAlign: "left",
                color: t.accent, fontFamily: t.fontSans,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = t.sidebarHover; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              <Icon d={ICONS.globe} size={13} />
              <span style={{ fontSize: 12, fontWeight: 600 }}>All events</span>
              <span style={{
                marginLeft: "auto", fontSize: 10, color: t.sidebarTextMuted,
                fontFamily: t.fontMono, letterSpacing: "0.04em",
              }}>
                {nonArchivedEvents.length}
              </span>
            </button>

            {nonArchivedEvents.length > 0 && (
              <div style={{ height: 1, background: t.sidebarBorder, margin: "4px 0" }} />
            )}

            {nonArchivedEvents.length === 0 ? (
              <div style={{ padding: "10px 14px", fontSize: 12, color: t.sidebarTextMuted, fontStyle: "italic" }}>
                Aucun événement
              </div>
            ) : (
              <div style={{
                fontSize: 9, color: t.sidebarTextMuted, fontFamily: t.fontMono,
                fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase",
                padding: "6px 10px 4px",
              }}>
                Switch to
              </div>
            )}

            {nonArchivedEvents.map(ev => {
              const isSelected = ev.id === activeEvent?.id;
              return (
                <button
                  key={ev.id}
                  onClick={() => switchToEvent(ev.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    width: "100%", padding: "9px 10px",
                    background: isSelected ? t.sidebarActiveBg : "transparent",
                    border: "none", borderRadius: 6, cursor: "pointer", textAlign: "left",
                    color: t.sidebarText, fontFamily: t.fontSans,
                    marginBottom: 2,
                  }}
                  onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = t.sidebarHover; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isSelected ? t.sidebarActiveBg : "transparent"; }}
                >
                  <div style={{
                    width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                    background: ev.status === "active" ? t.success : t.sidebarTextMuted,
                    boxShadow: ev.status === "active" ? `0 0 6px ${t.success}80` : "none",
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 12, fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? "#fff" : t.sidebarText,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {ev.name}
                    </div>
                    <div style={{ fontSize: 10, color: t.sidebarTextMuted, marginTop: 1 }}>
                      {new Date(ev.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}
                      {ev.city ? ` · ${ev.city}` : ""}
                    </div>
                  </div>
                  {isSelected && <Icon d={ICONS.check} size={12} color={t.accent} />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Nav — event-scoped on top, organisation below a separator */}
      <nav style={{ padding: "0 8px 10px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1 }}>
          {EVENT_NAV.map(n => <SidebarLink key={n.to} {...n} />)}

          {/* Exports — opens the slide-in panel instead of navigating. */}
          <SidebarButton
            label="Exports"
            icon="download"
            onClick={() => setExportsOpen(true)}
          />
        </div>

        <div style={{
          height: 1, background: t.sidebarBorder,
          margin: "10px 2px",
        }} />

        <div>
          {ORG_NAV.map(n => <SidebarLink key={n.to} {...n} />)}
        </div>
      </nav>

      {/* User */}
      <div style={{
        padding: "10px 14px",
        display: "flex", alignItems: "center", gap: 10,
        borderTop: `1px solid ${t.sidebarBorder}`,
        background: "rgba(0, 0, 0, 0.15)",
      }}>
        <NavLink
          to="/profile"
          title="My profile"
          style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0, textDecoration: "none" }}
        >
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={`${user?.firstName} ${user?.lastName}`}
              style={{
                width: 30, height: 30, borderRadius: "50%", objectFit: "cover",
                flexShrink: 0, border: `1px solid ${t.sidebarBorder}`,
              }}
            />
          ) : (
            user && <Avatar name={`${user.firstName} ${user.lastName}`} size={30} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12, fontWeight: 600, color: "#fff",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              letterSpacing: "-0.01em",
            }}>
              {user?.firstName} {user?.lastName}
            </div>
            <div style={{ fontSize: 10, color: t.sidebarTextMuted, fontFamily: t.fontMono, letterSpacing: "0.04em" }}>
              MY PROFILE
            </div>
          </div>
        </NavLink>
        <button
          onClick={signOut}
          title="Sign out"
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: t.sidebarTextMuted, padding: 6, borderRadius: 6,
            transition: `color ${t.transitionFast}, background ${t.transitionFast}`,
            display: "flex", flexShrink: 0,
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.color = t.danger;
            (e.currentTarget as HTMLElement).style.background = "rgba(239, 68, 68, 0.12)";
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.color = t.sidebarTextMuted;
            (e.currentTarget as HTMLElement).style.background = "transparent";
          }}
        >
          <Icon d={ICONS.logout} size={14} />
        </button>
      </div>
    </aside>
  );

  return (
    <div style={{
      display: "flex", height: "100vh",
      background: t.bg, color: t.text,
      fontFamily: t.fontSans, overflow: "hidden",
    }}>
      {/* Drawer backdrop (mobile only, when open) */}
      {isMobile && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 55,
            background: "rgba(15, 23, 42, 0.5)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            animation: "modal-backdrop-in 160ms cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        />
      )}

      {/* Sidebar */}
      {sidebarVisible && sidebar}

      {/* Content */}
      <div
        style={{ flex: 1, overflow: "auto", background: t.bg }}
        onClick={() => showSwitcher && setShowSwitcher(false)}
      >
        {/* Mobile top bar with hamburger + brand */}
        {isMobile && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "12px 16px",
            background: t.surface,
            borderBottom: `1px solid ${t.border}`,
            position: "sticky", top: 0, zIndex: 40,
          }}>
            <button
              onClick={() => setDrawerOpen(true)}
              title="Open menu"
              style={{
                background: "transparent", border: `1px solid ${t.border}`,
                borderRadius: t.radiusSm, cursor: "pointer",
                color: t.text, padding: "7px 10px",
                display: "flex", alignItems: "center",
              }}
            >
              <Icon d={ICONS.list} size={16} />
            </button>
            <div style={{
              width: 26, height: 26, borderRadius: 6,
              background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 800, fontSize: 13,
              fontFamily: t.fontSans, letterSpacing: "-0.02em",
              boxShadow: "0 3px 10px rgba(139, 92, 246, 0.32)",
            }}>
              E
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: t.text, letterSpacing: "-0.02em" }}>
              EventFlow AI
            </span>
            {activeEvent && (
              <span style={{
                marginLeft: "auto", fontSize: 11, color: t.muted,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "40%",
              }}>
                {activeEvent.name}
              </span>
            )}
          </div>
        )}

        <div style={{
          padding: isMobile ? "16px" : "24px 32px",
          maxWidth: 1280, margin: "0 auto",
        }}>
          <Suspense fallback={
            <div style={{ display: "flex", justifyContent: "center", padding: "96px 0" }}>
              <Spinner size={28} />
            </div>
          }>
            <Outlet context={{ activeEvent, events, switchToEvent, openExports: () => setExportsOpen(true) }} />
          </Suspense>
        </div>
      </div>

      {/* Global slide-in exports panel */}
      <ExportsPanel
        open={exportsOpen}
        onClose={() => setExportsOpen(false)}
        event={activeEvent}
      />
    </div>
  );
}

function SidebarButton({ label, icon, onClick }: { label: string; icon: string; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%",
        padding: "8px 12px", borderRadius: t.radiusMd, border: "none",
        background: hover ? t.sidebarHover : "transparent",
        color: t.sidebarText, cursor: "pointer", textAlign: "left",
        fontSize: 13, fontWeight: 500, fontFamily: t.fontSans,
        marginBottom: 2, transition: `background ${t.transitionFast}`,
      }}
    >
      <Icon d={ICONS[icon]} size={15} />
      {label}
    </button>
  );
}

function SidebarLink({ to, label, icon, end }: { to: string; label: string; icon: string; end: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        display: "flex", alignItems: "center", gap: 10,
        width: "100%", padding: "9px 10px",
        borderRadius: t.radiusMd, border: "none",
        background: isActive ? t.sidebarActiveBg : "transparent",
        color: isActive ? "#fff" : t.sidebarText,
        fontSize: 13, fontWeight: isActive ? 600 : 500,
        cursor: "pointer", fontFamily: t.fontSans,
        marginBottom: 2,
        transition: `background ${t.transitionFast}, color ${t.transitionFast}`,
        textDecoration: "none",
        position: "relative",
        boxShadow: isActive ? "0 0 0 1px rgba(99, 102, 241, 0.32) inset, 0 0 16px rgba(99, 102, 241, 0.18)" : "none",
      })}
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLElement;
        if (!el.style.background.includes("99, 102, 241")) el.style.background = t.sidebarHover;
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLElement;
        const isActive = el.getAttribute("aria-current") === "page";
        el.style.background = isActive ? t.sidebarActiveBg : "transparent";
      }}
    >
      <Icon d={ICONS[icon]} size={15} />
      {label}
    </NavLink>
  );
}
